import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useIsDesktop } from "../../hooks/useMedia";
import { scrollToId, focusWaitlist } from "../../lib/scroll";
import { WaitlistForm } from "./WaitlistForm";
import { Words } from "../Words";

const STORES = [
  { id: "app-store", label: "Download on the App Store", url: process.env.REACT_APP_APP_STORE_URL },
  { id: "play-store", label: "Get it on Google Play", url: process.env.REACT_APP_PLAY_STORE_URL },
].filter((s) => s.url);

export default function JoinSection() {
  const ref = useRef(null);
  const desktop = useIsDesktop();
  const reduce = useReducedMotion();
  const animated = desktop && !reduce;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "start start"] });
  const still = useTransform(scrollYProgress, () => 1);
  const p = animated ? scrollYProgress : still;
  const bgY = useTransform(p, [0, 1], [80, 0]);
  const headScale = useTransform(p, [0.3, 1], [0.94, 1]);
  const ctaOpacity = useTransform(p, [0.5, 0.95], [0.3, 1]);

  return (
    <section ref={ref} id="join" data-testid="section-join" className="relative overflow-hidden py-28 lg:min-h-screen lg:py-36">
      <motion.div style={{ y: bgY }} className="absolute inset-x-0 -bottom-20 -top-20 overflow-hidden" aria-hidden data-testid="join-bg">
        <div className="hero-bg ambient-drift absolute" />
      </motion.div>
      <div className="join-fade pointer-events-none absolute inset-x-0 top-0 h-40" aria-hidden />
      <div className="relative z-10 mx-auto max-w-[900px] px-5 text-center">
        <p className="eyebrow">09 · Join the waitlist</p>
        <motion.h2 style={{ scale: headScale }} className="h-hero mt-4 text-[#08090D] xl:whitespace-nowrap" data-testid="join-headline">
          <Words text="The Future" />
          <br />
          <Words text="Is Connected." start={2} className="text-gradient" />
        </motion.h2>
        <p className="mx-auto mt-6 max-w-[520px] text-[18px] leading-relaxed text-[#2f3242] sm:text-[20px]">
          Less scrolling. More conversations. More experiences. More human connection.
        </p>
        <motion.div style={{ opacity: ctaOpacity }} className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button type="button" onClick={focusWaitlist} data-testid="join-future-button" className="btn-primary h-14 px-8 text-[16px]">
            Join the Future <ArrowRight size={18} aria-hidden />
          </button>
          <button type="button" onClick={() => scrollToId("experience")} data-testid="join-explore-button" className="btn-glass glass glass-light h-14 px-8 text-[16px]">
            Explore Social AI
          </button>
        </motion.div>
        <div className="glass glass-heavy mx-auto mt-10 max-w-[600px] rounded-[36px] p-5 sm:p-8">
          <WaitlistForm />
        </div>
        {STORES.length > 0 && (
          <div className="mt-6 flex justify-center gap-3" data-testid="store-buttons">
            {STORES.map((s) => (
              <a key={s.id} href={s.url} target="_blank" rel="noreferrer" data-testid={`store-${s.id}`} className="btn-primary h-12 px-5 text-[14px]">{s.label}</a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
